import type { ReactNode } from 'react';
import { cn } from '@immo/ui-kit';

const PLACEHOLDER_ATTRIBUTE = 'data-placeholder';
const FILL_FIELD_ATTRIBUTE = 'data-fill-field';
const LINK_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:']);
const CHIP_CLASS = 'rounded-sm border border-border bg-subtle px-1 font-mono text-xs text-text-secondary';

export type PlaceholderLabel = (key: string) => string;

type Props = {
  readonly markup: string;
  /** The German name without braces; the chip adds `{…}`. */
  readonly placeholderLabel: PlaceholderLabel;
  readonly className?: string;
};

const isLinkAllowed = (href: string): boolean => {
  try {
    return LINK_PROTOCOLS.has(new URL(href).protocol);
  } catch {
    return false;
  }
};

function renderChip(element: Element, path: string, placeholderLabel: PlaceholderLabel): ReactNode | null {
  const placeholder = element.getAttribute(PLACEHOLDER_ATTRIBUTE);
  if (placeholder !== null) {
    return (
      <span key={path} data-chip="placeholder" className={CHIP_CLASS}>
        {`{${placeholderLabel(placeholder)}}`}
      </span>
    );
  }

  const fillField = element.getAttribute(FILL_FIELD_ATTRIBUTE);
  if (fillField === null) return null;

  return (
    <span key={path} data-chip="fill-field" className={cn(CHIP_CLASS, 'border-dashed')}>
      {`[${fillField}]`}
    </span>
  );
}

function renderChildren(parent: Node, path: string, placeholderLabel: PlaceholderLabel): ReactNode[] {
  return Array.from(parent.childNodes).map((child, index) => renderNode(child, `${path}.${index}`, placeholderLabel));
}

function renderLink(element: Element, path: string, placeholderLabel: PlaceholderLabel): ReactNode {
  const href = element.getAttribute('href');
  const children = renderChildren(element, path, placeholderLabel);

  if (href === null || !isLinkAllowed(href)) return <a key={path}>{children}</a>;

  return (
    <a key={path} href={href} target="_blank" rel="noopener noreferrer" className="text-interactive underline">
      {children}
    </a>
  );
}

function renderElement(element: Element, path: string, placeholderLabel: PlaceholderLabel): ReactNode {
  const chip = element.tagName === 'SPAN' ? renderChip(element, path, placeholderLabel) : null;
  if (chip !== null) return chip;

  const children = () => renderChildren(element, path, placeholderLabel);

  switch (element.tagName) {
    case 'DIV':
      return <div key={path}>{children()}</div>;
    case 'P':
      return <p key={path}>{children()}</p>;
    case 'BR':
      return <br key={path} />;
    case 'B':
    case 'STRONG':
      return <strong key={path}>{children()}</strong>;
    case 'I':
    case 'EM':
      return <em key={path}>{children()}</em>;
    case 'U':
      return <u key={path}>{children()}</u>;
    case 'UL':
      return (
        <ul key={path} className="list-disc pl-5">
          {children()}
        </ul>
      );
    case 'OL':
      return (
        <ol key={path} className="list-decimal pl-5">
          {children()}
        </ol>
      );
    case 'LI':
      return <li key={path}>{children()}</li>;
    case 'A':
      return renderLink(element, path, placeholderLabel);
    default:
      return element.textContent;
  }
}

function renderNode(node: Node, path: string, placeholderLabel: PlaceholderLabel): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  return renderElement(node as Element, path, placeholderLabel);
}

/**
 * Renders a stored E-Mail-Vorlage read-only. It builds React elements from an
 * allow-list and never injects HTML, so the markup's origin does not matter:
 * any other element gives its text, and a link keeps its `href` only for
 * http, https and mailto.
 */
export function EmailTemplateMarkup({ markup, placeholderLabel, className }: Props) {
  const body = new DOMParser().parseFromString(markup, 'text/html').body;

  return (
    <div className={cn('space-y-2 text-base-sm text-text-primary', className)}>
      {renderChildren(body, 'n', placeholderLabel)}
    </div>
  );
}
